import { CatalogsService } from '@/common/services/swagger/CatalogsService';
import { CatalogsJobsService } from '@/common/services/swagger/CatalogsJobsService';
import { ClassificationsService } from '@/common/services/swagger/ClassificationsService';

import { ImagesService } from '@/common/services/swagger/ImagesService';
import {
  AppSettings,
  AutocompleteSearchResult,
  FilterSettings,
  FilterValueSearchResult,
  ImageSearchResult,
  PrmRelationshipTypeEnum,
  serviceOptions,
  SortDirectionEnum,
  TranslationItem,
  ClassificationNodesSearchResult,
  CatalogAppSettings,
  CatalogSearchTypeEnum,
  CatalogSortFieldEnum,
  CatalogProductSearchTypeEnum,
  CatalogProductSortFieldEnum,
  CatalogProductSearchDataTypeEnum,
  CatalogId,
  CatalogActionResult,
  ExportResult,
  CatalogExportPriceSettings,
  JobSearchResult,
  JobAction,
  CatalogAutocompleteSearchResult,
  JobSortSettings,
  CatalogProductUpdateItem,
  CatalogProductDeleteItem,
  CatalogSearchResult,
} from '@/common/services/swagger/index.defs';
import { ProductsService } from '@/common/services/swagger/ProductsService';
import { TranslationsService } from '@/common/services/swagger/TranslationsService';
import axios from 'axios';
import { reactive } from 'vue';
import { DataType } from '../configuration/application/DataType';
import { Instance } from '../Instance';
import { IErrorEvent, ILoadEvent } from './ILoadEvent';
import { IRouteData } from './IRouteData';
import { StoreService } from './StoreService';
import { CatalogsProductsService } from '@/common/services/swagger/CatalogsProductsService';
import { IHttpService } from '@/common/api/runtime/IHttpService';
import { InstanceType } from '@/common/api/runtime/IInstance';
import { CatalogsAliasesService } from '@/common/services/swagger/CatalogsAliasesService';
import { NotificationType } from '@/common/api/runtime/INotification';
export interface PrmLoadArgs {
  productId: string;
  relationshipType: PrmRelationshipTypeEnum;
  page: number;
  pageSize: number;
  filter?: string;
}

export class HttpService implements IHttpService {
  instanceType: InstanceType = 'Catalogs';
  private storeService: StoreService;

  constructor(private instance: Instance, baseURL?: string, accessToken?: string) {
    const axiosInstance = axios.create({
      baseURL,
      timeout: 60000,
    });

    axiosInstance.interceptors.request.use(
      (config) => {
        if (config && config.headers) {
          const currentAccessToken = instance.store?.options?.accessToken;
          config.headers['Authorization'] = `Bearer ${currentAccessToken || accessToken}`;

          if (instance.props['dashboard'] === true) {
            config.headers['dashboard'] = 'true';
          }
        }
        return config;
      },
      (error) => {
        // Do something with request error
        return Promise.reject(error);
      },
    );

    axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error?.message == 'canceled') {
          return Promise.reject(error);
        }

        const errorData = error?.response?.data;

        const errorEvent: IErrorEvent = {
          type: 'ServerError',
          message: error.message,
          isHandled: false,
        };

        if (errorData) {
          errorEvent.message = errorData.Message;
          errorEvent.type = errorData.Type;
          errorEvent.traceId = errorData.TraceId;
        }

        if (instance.store.options.errorInterceptor) {
          const result = instance.store.options.errorInterceptor(errorEvent);
          if (!result.isHandled) {
            instance.store.notifications.push({
              type: NotificationType.danger,
              message: result.message || 'Unknown error',
            });
          }
        } else {
          instance.store.notifications.push({
            type: NotificationType.danger,
            message: errorEvent.message || 'Unknown error',
          });
        }

        return Promise.reject(error);
      },
    );

    serviceOptions.axios = axiosInstance;

    this.storeService = new StoreService(instance);
  }

  public async load(newRoute?: IRouteData, paginate?: boolean): Promise<void> {
    if (!this.instance.router) return;

    const route = newRoute || this.instance.router.routeData;
    switch (route.view) {
      case 'catalogs':
        return await this.queryCatalogs(route);
      case 'products':
        return await this.queryProducts(route, paginate);
      case 'aliases':
        return await this.queryProducts(route, paginate);

      default:
        throw new Error(`Unknown view type "${route.view}" to be handled by HttpService`);
    }
  }

  public async queryCatalogs({ catalogs }: IRouteData): Promise<void> {
    this.instance.store.data.isLoadingData = true;

    const result = await CatalogsService.search({
      body: {
        appSettings: this.instance.store?.options.application as CatalogAppSettings,
        pager: {
          page: catalogs.pagination.page,
          pageSize: catalogs.pagination.pageSize,
        },
        search: {
          searchText: catalogs.searchText ?? undefined,
          searchType: catalogs.searchType ?? CatalogSearchTypeEnum.CatalogCodeAndDescription,
          createdWithinLastDays: catalogs.createdWithinLastDays,
        },
        sort: {
          field: catalogs.sortField ?? CatalogSortFieldEnum.LastModified, //   // field: sortField ?? SortFieldEnum.Product,
          direction: catalogs.sortDirection ?? SortDirectionEnum.Descending,
        },
      },
    });

    this.instance.eventBus.emit('search-loaded', result);

    if (result) {
      this.storeService.setData(DataType.Catalogs, result);
    }

    this.instance.store.data.isLoadingData = false;
    this.instance.eventBus.emit('search-changed');
  }

  public async queryProducts({ products }: IRouteData, paginate?: boolean): Promise<void> {
    this.instance.store.data.isLoadingData = true;

    const result = await CatalogsProductsService.search({
      body: {
        appSettings: this.instance.store?.options.application as CatalogAppSettings,
        //dataTypes: this.instance.getRegisteredSearchDataTypes(),
        pager: {
          page: products.pagination.page,
          pageSize: products.pagination.pageSize,
        },
        search: {
          searchText: products.searchText ?? undefined,
          searchType: products.searchType ?? CatalogProductSearchTypeEnum.WithAllWords,
          cid: products.cid,
          treeType: products.treeType,
          catalogId: products.catalog,
          filterSettings: products.filters,
        },
        sort: {
          field: products.sortField ?? CatalogProductSortFieldEnum.LastImport, //   // field: sortField ?? SortFieldEnum.Product,
          direction: products.sortDirection ?? SortDirectionEnum.Ascending,
        },
        dataTypes: paginate
          ? [CatalogProductSearchDataTypeEnum.Products]
          : [
            CatalogProductSearchDataTypeEnum.Products,
            CatalogProductSearchDataTypeEnum.Filters,
            CatalogProductSearchDataTypeEnum.Breadcrumbs,
            CatalogProductSearchDataTypeEnum.ProductClassifications,
            CatalogProductSearchDataTypeEnum.PartClassifications,
          ], // TODO
      },
    });

    this.instance.eventBus.emit('search-loaded', result);

    if (result.products) {
      this.storeService.setData(DataType.Products, result.products);
    }

    if (!paginate) {
      if (result.productClassifications) {
        this.storeService.setData(DataType.ProductClassifications, result.productClassifications);
      }

      if (result.partClassifications) {
        this.storeService.setData(DataType.PartClassifications, result.partClassifications);
      }

      if (result.filters) {
        this.storeService.setData(DataType.Filters, result.filters);
      }

      if (result.breadcrumbs) {
        this.storeService.setData(DataType.Breadcrumbs, result.breadcrumbs);
      }
    }

    this.instance.store.data.isLoadingData = false;
    this.instance.eventBus.emit('search-changed');
  }

  public async preview(productId: string): Promise<void> {
    this.instance.store.data.isLoadingData = true;

    const result = await ProductsService.preview({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        search: {
          productId,
        },
      },
    });
    this.instance.eventBus.emit('preview-loaded', result);
    if (result) {
      this.storeService.setData(DataType.ProductPreview, result);
    }

    this.instance.store.data.isLoadingData = false;
  }

  public async filterValues(
    attributeCodes: string[],
    catalogId?: CatalogId,
  ): Promise<FilterValueSearchResult> {
    const { products } = this.instance.router.routeData;

    return await CatalogsProductsService.filters({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,

        search: {
          cid: products.cid,
          treeType: products.treeType,
          searchText: products.searchText,
          searchType: products.searchType || CatalogProductSearchTypeEnum.WithAllWords, // TODO: searchType as SearchTypeEnum,
          attributeCodes,
          filterSettings: products.filters,
          catalogId: catalogId,
        },
      },
    });
  }

  public async loadImages(products: string[]): Promise<ImageSearchResult | undefined> {
    try {
      return await ImagesService.getByProductIds({
        body: {
          appSettings: this.instance.store?.options.application as AppSettings,
          search: {
            products,
          },
        },
      });
    } catch (exception) {
      this.instance.logger.log('Failed to fetch images', exception);
    }
  }

  public async loadImagesById(imageIds: string[]): Promise<ImageSearchResult | undefined> {
    try {
      return await ImagesService.getByImageIds({
        body: {
          appSettings: this.instance.store?.options.application as AppSettings,
          search: {
            imageIds,
          },
        },
      });
    } catch (exception) {
      this.instance.logger.log('Failed to fetch images', exception);
    }
  }

  public async loadTranslations(): Promise<TranslationItem[] | undefined> {
    const resp = await TranslationsService.translations({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        components: [],
      },
    });

    const interceptFunc = this.instance.store?.options.dataInterceptor;

    const loadEvent: ILoadEvent = {
      type: DataType.Translations,
      data: reactive({ items: resp }),
      instance: this.instance,
    };

    if (interceptFunc) {
      const inc = await interceptFunc(loadEvent);
      return inc.data.items;
    } else {
      return resp;
    }
  }

  public async autocomplete(searchText: string): Promise<AutocompleteSearchResult> {
    const { products } = this.instance.router.routeData;

    const result = await CatalogsProductsService.autocomplete({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        pager: {
          page: 1,
          pageSize: 5,
        },
        search: {
          cid: products.cid,
          treeType: products.treeType,
          searchType: products.searchType || CatalogProductSearchTypeEnum.WithAllWords,
          searchText: searchText ?? undefined,
          filterSettings: products.filters,
          catalogId: products.catalog,
        },
      },
    });

    return result;
  }

  public async autocompleteCatalogs(searchText: string): Promise<CatalogAutocompleteSearchResult> {
    const { catalogs } = this.instance.router.routeData;

    const result = await CatalogsService.autocomplete({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        pager: {
          page: 1,
          pageSize: 5,
        },
        search: {
          searchType: catalogs.searchType || CatalogSearchTypeEnum.CatalogCodeAndDescription,
          searchText: searchText ?? undefined,
        },
      },
    });

    return result;
  }

  public async loadFiltersResultCount(
    filterSettings: FilterSettings[],
  ): Promise<AutocompleteSearchResult> {
    const { products } = this.instance.router.routeData;

    const result = await CatalogsProductsService.autocomplete({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        pager: {
          page: 1,
          pageSize: 5,
        },
        search: {
          cid: products.cid,
          treeType: products.treeType,
          searchType: products.searchType || CatalogProductSearchTypeEnum.WithAllWords,
          searchText: products.searchText,
          filterSettings,
          catalogId: products.catalog,
        },
      },
    });

    return result;
  }

  public async loadClassificationsNodes(cids: string[]): Promise<ClassificationNodesSearchResult> {
    const result = await ClassificationsService.nodes({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        search: {
          cids,
        },
      },
    });

    return result;
  }

  public async exportCatalog(
    catalogId: CatalogId,
    priceSettings?: CatalogExportPriceSettings,
  ): Promise<ExportResult> {
    return CatalogsService.export({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        catalogId,
        priceSettings,
      },
    });
  }

  public async downloadCatalog(guid: string): Promise<unknown> {
    return CatalogsService.download({
      guid,
    });
  }

  public async importCatalog(
    catalogId: CatalogId,
    fileContentBase64: string,
    fileName: string,
  ): Promise<CatalogActionResult> {
    return CatalogsService.import({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        catalogId,
        fileContentBase64,
        fileName,
      },
    });
  }

  public async copyCatalog(
    sourceCatalogId: CatalogId,
    targetCatalogId: CatalogId,
  ): Promise<CatalogActionResult> {
    return CatalogsService.copy({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        sourceCatalogId,
        targetCatalogId,
      },
    });
  }

  public async addCatalog(catalogId: CatalogId, description: string): Promise<CatalogActionResult> {
    return CatalogsService.add({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        catalogId,
        description,
        default: false,
      },
    });
  }

  public async updateCatalog(
    catalogId: CatalogId,
    description: string,
    isDefault?: boolean,
  ): Promise<CatalogActionResult> {
    return CatalogsService.update({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        catalogId,
        description,
        default: isDefault,
      },
    });
  }

  public async updateDefaultCatalog(
    catalogId: CatalogId,
    isDefault: boolean,
  ): Promise<CatalogActionResult> {
    return CatalogsService.update({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        catalogId,
        default: isDefault,
      },
    });
  }

  public async deleteCatalog(catalogId: CatalogId): Promise<CatalogActionResult> {
    return CatalogsService.delete({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        catalogId,
      },
    });
  }

  public async downloadJobHistory(sortSettings?: JobSortSettings): Promise<JobSearchResult> {
    return CatalogsJobsService.search({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        sort: sortSettings || undefined,
      },
    });
  }

  public async exportJobHistory(
    jobAction: Pick<JobAction, 'catalogId' | 'jobStatus' | 'jobType'>,
  ): Promise<ExportResult> {
    return CatalogsJobsService.export({
      body: {
        ...jobAction,
        appSettings: this.instance.store?.options.application as AppSettings,
      },
    });
  }

  public async getConfigurationOption(
    configuredProductId: string,
    productId: string,
    unitOfMeasure: string,
    catalogId: CatalogId,
  ) {
    return await CatalogsProductsService.configurationOption({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        configuredProductId,
        productId,
        catalogId,
        unitOfMeasure,
      },
    });
  }

  public async importCatalogAliases(
    fileContentBase64: string,
    fileName: string,
  ): Promise<CatalogActionResult> {
    return CatalogsAliasesService.import({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        fileContentBase64,
        fileName,
      },
    });
  }

  public async exportCatalogAliases(): Promise<ExportResult> {
    return CatalogsAliasesService.export({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
      },
    });
  }

  public async updateCatalogProduct(
    product: CatalogProductUpdateItem,
    catalogId: CatalogId,
  ): Promise<CatalogActionResult> {
    return CatalogsProductsService.update({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        product,
        catalogId,
      },
    });
  }

  public async deleteCatalogProduct(
    products: CatalogProductDeleteItem[],
    catalogId: CatalogId,
  ): Promise<CatalogActionResult> {
    return CatalogsProductsService.delete({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        catalogId,
        products,
      },
    });
  }

  public async copyCatalogProduct(
    productId: string,
    unitOfMeasure: string,
    sourceCatalogId: CatalogId,
    targetCatalogId: CatalogId,
  ): Promise<CatalogActionResult> {
    return CatalogsProductsService.copy({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        sourceCatalogId,
        targetCatalogId,
        productId,
        unitOfMeasure,
      },
    });
  }

  public async getAllCatalogs(): Promise<CatalogSearchResult> {
    return CatalogsService.search({
      body: {
        appSettings: this.instance.store?.options.application as AppSettings,
        /** @todo Remove backend limit */
        pager: { page: 1, pageSize: 1000 },
      },
    });
  }
}
